// src/pages/DashboardsByConfigPage.tsx
import { getBookmarksConfigValueId } from '@/components/utils/CorgeeHelper';
/**
 * Dashboards by ConfigValueId Page
 * Generic dashboard view that loads dashboards based on configValueId from URL
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { authStorage, dashboardStorage } from '@/lib/storage';
import TabControl from '@/components/TabControl/TabControl';
import { FilterBar } from '@/components/Filter/FilterBar';
import { SearchBar } from '@/components/Filter/SearchBar';
import { DashboardGroups } from '@/components/DashboardGroups/DashboardGroups';
import {
  DashboardViewSelector,
  type DashboardViewType,
} from '@/components/Dashboard/DashboardViewSelector';
import { RefreshNotificationButton } from '@/components/Dashboard/RefreshNotificationButton';
import { useSocketRefreshButton, getMoveToTopConversationIds } from '@/hooks/useSocketRefreshButton';
import { useInfoWindow } from '@/contexts/InfoWindowContext';
import { filterService } from '@/services/filterService';
import { useFilterPersistence } from '@/hooks/useFilterPersistence';
import type { TabControlConfig, ReactFunctions, Dashboard } from '@/components/TabControl/types';
import type { FilterOption } from '@/types/dashboard';
import type { DashboardGroupData } from '@/components/DashboardGroups/types';
import type { ConversationDetail } from '@/types/conversation';
import { searchHistory } from '@/lib/searchHistory';
import { isDashboardPersonal, setDashboardPersonal } from '@/lib/personalFilterConfig';
import { menuService } from '@/services/menuService';
import { dashboardApi, MIN_COLUMN_WIDTH_PX, gridSizeToColumnWidth } from '@/services/dashboardApi';
import { useDashboardViewSync } from '@/hooks/useDashboardViewSync';
import { dashboardService } from '@/services/dashboardService';
import { CalendarView } from '@/components/CalendarView';
import { GalleryView } from '@/components/Dashboard/GalleryView';
import { FilesView } from '@/components/Dashboard/FilesView';
import { GridView } from '@/components/GridView/GridView';
import type { GridViewRef } from '@/components/GridView/types';
import Gantt from '@/components/Gantt/Gantt';
import type { GanttConfig } from '@/components/Gantt/Gantt';
import { PhaseDetails } from '@/components/PhaseDetails/PhaseDetails';
import type { PhaseDetailsRef } from '@/components/PhaseDetails/types';
import { PhaseDetailsV2 } from '@/components/PhaseDetailsV2/PhaseDetailsV2';
import type { PhaseDetailsV2Ref } from '@/components/PhaseDetailsV2/types';

export const DashboardsByConfigPage: React.FC = () =>
{
  const { configValueId: configValueIdParam } = useParams<{ configValueId: string }>();
  const configValueId = parseInt(configValueIdParam || '1', 10);

  const { user: _user, IsAdmin } = useAuth();
  void _user;
  const socketConnectionId = useSocketConnectionId();
  const location = useLocation();
  const navigate = useNavigate();

  // Shared dashboard — populated when arriving from a "Copy link" redirect
  const sharedDashboardId: number | null = (location.state as any)?.sharedDashboardId ?? null;
  const [isSharedPublic, setIsSharedPublic] = useState<boolean | null>(null);

  useEffect(() =>
  {
    if (!sharedDashboardId) return;
    dashboardApi
      .getDashboardById(sharedDashboardId)
      .then(d => setIsSharedPublic(d?.IsPublic ?? false))
      .catch(() => setIsSharedPublic(false));
  }, [sharedDashboardId]);

  // Menu section name for search history source label
  const [sectionName, setSectionName] = useState<string>('DASHBOARDS');

  // Dashboard state - TabControl handles loading and selection
  const [currentDashboard, setCurrentDashboard] = useState<Dashboard | null>(null);
  const [pinnedDashboards, setPinnedDashboards] = useState<Dashboard[]>([]);

  // Filter persistence (UI chips + applied filters + localStorage)
  const {
    filtersSelected,
    appliedFilters,
    handleFilterSelect,
    handleFilterUnselect,
    handleApplyFilters,
  } = useFilterPersistence(`C2_Filters_Config_${configValueId}`);

  const [isApplyForAll, setIsApplyForAll] = useState(() =>
    !isDashboardPersonal(currentDashboard?.DashboardId ?? 0),
  );

  // Derived state: update isApplyForAll synchronously during the render when the active
  // dashboard changes. A useEffect would run after commit — by then DashboardGroups has
  // already fired an API call with the previous dashboard's stale IsFilterPersonal value.
  const [prevDashboardIdForApplyForAll, setPrevDashboardIdForApplyForAll] = useState(
    currentDashboard?.DashboardId ?? null,
  );
  if ((currentDashboard?.DashboardId ?? null) !== prevDashboardIdForApplyForAll)
  {
    setPrevDashboardIdForApplyForAll(currentDashboard?.DashboardId ?? null);
    setIsApplyForAll(!isDashboardPersonal(currentDashboard?.DashboardId ?? 0));
  }

  const handleApplyFiltersWrapper = useCallback((applyForAll: boolean) =>
  {
    setIsApplyForAll(applyForAll);
    if (currentDashboard?.DashboardId)
    {
      setDashboardPersonal(currentDashboard.DashboardId, !applyForAll);
    }
    handleApplyFilters();
  }, [handleApplyFilters, currentDashboard?.DashboardId]);

  // Extract boolean/toggle filters (Id "0" or "1") as top-level API params (e.g. IsRedFlag: 1)
  const extraFilterParams = useMemo(() =>
  {
    const extra: Record<string, any> = {};
    const customFields: any[] = [];
    appliedFilters.forEach(f =>
    {
      if (f.IsCustomField)
      {
        customFields.push({
          Name: f.FieldName,
          FieldTypeId: f.FieldTypeId,
          Value: f.Id,
          ComparisonOperator: f.ComparisonOperator || null,
        });
      }
      else
      {
        const num = parseInt(String(f.Id), 10);
        extra[f.FieldName] = isNaN(num) ? f.Id : num;
      }
    });
    if (customFields.length > 0)
    {
      extra.CustomFields = customFields;
    }
    extra.IsFilterPersonal = !isApplyForAll;
    return extra;
  }, [appliedFilters, isApplyForAll]);

  // Serialize appliedFilters to the JSON format expected by the API
  const filtersJson = useMemo(() =>
  {
    const allFilters = appliedFilters.map(f => ({
      Name: f.FieldName,
      IsCustomField: f.IsCustomField,
      FieldTypeId: f.FieldTypeId,
      CustomFields: f.CustomFields || null,
      ComparisonOperator: f.ComparisonOperator || null,
      isChipFilter: null,
      isSelected: true,
      Text: f.Name,
      Value: f.Id,
    }));
    return JSON.stringify(allFilters);
  }, [appliedFilters]);

  // Filter state
  const [filter, setFilter] = useState<string>('');
  const [filterOptions, setFilterOptions] = useState<FilterOption[]>([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState<string | null>(null);

  // Search state
  const [searchText, setSearchText] = useState<string>('');

  // View mode state
  const [columnWidth, setColumnWidth] = useState(350);
  const [dashboardViewTypeId, setDashboardViewTypeId] = useState<DashboardViewType>(1);
  const { persistDashboardView, persistDashboardViewDebounced } = useDashboardViewSync();
  const [pageSize] = useState(130);
  const [showAllGalleryImages, setShowAllGalleryImages] = useState(false);

  // Derive cardViewMode from dashboardViewTypeId
  const cardViewMode: 'compact' | 'detailed' = dashboardViewTypeId === 2 ? 'detailed' : 'compact';

  // Gantt View state
  const [enableWeekend, setEnableWeekend] = useState(false);
  const [selectedLayerId, setSelectedLayerId] = useState(0);
  const [ganttLayers, setGanttLayers] = useState<{ LayerId: number; Name: string }[]>([]);

  // Refs for TabControl
  const reactFunctionsRef = useRef<ReactFunctions | null>(null);
  const gridExportRef = useRef<GridViewRef>(null);
  const phaseDetailsExportRef = useRef<PhaseDetailsRef>(null);
  const phaseDetailsV2ExportRef = useRef<PhaseDetailsV2Ref>(null);

  // Ref to background-refresh DashboardGroups from socket notifications
  const dashboardGroupsRefreshRef = useRef<((conversationIds?: number[]) => void) | null>(null);

  // Check if current dashboard has dynamic groups (IsDefault=true)
  const hasDynamicGroups = useMemo(
    () => currentDashboard?.DashboardGroups?.some(g => g.IsDefault === true) ?? false,
    [currentDashboard],
  );

  // Socket refresh notifications
  const {
    hasPendingUpdates,
    pendingCount,
    pendingOrderDashboard,
    pendingDashboardList,
    triggerRefresh,
    clearConversationHighlight,
    pendingGroupIds,
    pendingConversationIds,
  } = useSocketRefreshButton({
    currentDashboardId: currentDashboard?.DashboardId ?? null,
    configValueId,
    hasDynamicGroups,
    onRefresh: updates => dashboardGroupsRefreshRef.current?.(getMoveToTopConversationIds(updates)),
    onRefreshDashboardList: () => reactFunctionsRef.current?.reloadDashboard(false),
    onRefreshDashboardOrder: () =>
      reactFunctionsRef.current?.reloadDashboard(false, undefined, true),
    onDashboardRemoved: () => reactFunctionsRef.current?.reloadDashboard(false),
  });

  // Get openInfoWindow from context
  const { openInfoWindow } = useInfoWindow();

  // Sync view resolution variable
  const [viewSyncDashboardId, setViewSyncDashboardId] = useState<number | null>(null);

  const isDark = document.documentElement.classList.contains('dark');

  // Resolve view type and grid size on dashboard switch
  // PRIORITY: localStorage stores user's last visited view per dashboard
  if (currentDashboard && currentDashboard.DashboardId !== viewSyncDashboardId)
  {
    setViewSyncDashboardId(currentDashboard.DashboardId);

    let newViewId: DashboardViewType = 1;
    let newGridSize = 350;

    const savedEntry = dashboardStorage.findByContext(
      0,
      configValueId,
      currentDashboard.DashboardId,
    );

    if (savedEntry?.GridSize)
    {
      newGridSize = Math.max(MIN_COLUMN_WIDTH_PX, savedEntry.GridSize);
    }
    else if (currentDashboard.GridSize)
    {
      newGridSize = gridSizeToColumnWidth(currentDashboard.GridSize);
    }

    if (savedEntry?.DashboardViewId)
    {
      // Use saved view from localStorage (user's last visit)
      newViewId = savedEntry.DashboardViewId as DashboardViewType;
    }
    else
    {
      // First-time visit: default to compact view (small cards)
      newViewId = 1;
    }

    setColumnWidth(newGridSize);
    setDashboardViewTypeId(newViewId);

    // Save inline with the correct newViewId (avoids stale ref from useEffect)
    const tabIndex = pinnedDashboards.findIndex(d => d.DashboardId === currentDashboard.DashboardId);
    dashboardStorage.updateDashboard({
      projectId: 0,
      configValueId: configValueId,
      index: tabIndex >= 0 ? tabIndex : 0,
      dashboardId: currentDashboard.DashboardId,
      DashboardViewId: newViewId,
      GridSize: newGridSize,
      lastModified: Date.now(),
    });
  }

  // Reset state when configValueId changes
  useEffect(() =>
  {
    setCurrentDashboard(null);
    setPinnedDashboards([]);
    setFilter('');
    setSearchText('');
  }, [configValueId]);

  // Fetch section name from menu for search history label
  useEffect(() =>
  {
    if (configValueId === getBookmarksConfigValueId())
    {
      setSectionName('BOOKMARKS');
      return;
    }
    menuService.getMenuByPerson().then(items =>
    {
      const item = items.find(i => i.ConfigValueID === configValueId);
      setSectionName(item?.Text?.toUpperCase() || 'DASHBOARDS');
    });
  }, [configValueId]);

  // Get API configuration
  const getApiConfig = useCallback(() =>
  {
    const token = authStorage.getAccessToken() || '';
    const apiUrl =
      import.meta.env.VITE_API_URL ||
      import.meta.env.VITE_JS_apiURL ||
      'http://localhost/Corgee.Web/';

    return {
      url: apiUrl,
      token: token,
      refreshToken: '',
      TenancyName: import.meta.env.VITE_TENANCY_NAME || 'cgbot',
    };
  }, []);

  // Load filter options when filter text changes
  // True once the user has typed in the filter input
  const filterTouchedRef = useRef<boolean>(false);

  useEffect(() =>
  {
    const loadFilterOptions = async () =>
    {
      if (filter.length > 0) filterTouchedRef.current = true;
      // Below the search threshold, reload the full list instead of clearing it
      const searchTerm = filter.length >= 2 ? filter : null;
      // On first mount the full list is loaded by onFilterOpened instead
      if (!searchTerm && !filterTouchedRef.current) return;

      try
      {
        setFilterLoading(true);
        setFilterError(null);
        const options = await filterService.getFilterOptions(searchTerm, {
          IsFromProject: false,
          ConfigValueId: configValueId,
        });
        setFilterOptions(options);
      }
      catch (err)
      {
        console.error('Error loading filter options:', err);
        setFilterError('Error loading filters');
      }
      finally
      {
        setFilterLoading(false);
      }
    };

    const debounceTimeout = setTimeout(loadFilterOptions, 300);
    return () => clearTimeout(debounceTimeout);
  }, [filter, configValueId]);

  // Load initial filter options when FilterIcon is clicked
  const handleFilterOpened = useCallback(async () =>
  {
    try
    {
      setFilterLoading(true);
      setFilterError(null);
      const options = await filterService.getFilterOptions(null, {
        IsFromProject: false,
        ConfigValueId: configValueId,
      });
      setFilterOptions(options);
    }
    catch (err)
    {
      console.error('Error loading filter options:', err);
      setFilterError('Error loading filters');
    }
    finally
    {
      setFilterLoading(false);
    }
  }, [configValueId]);

  // Refs to hold latest view/grid without stale closures
  const dashboardViewTypeIdRef = useRef<DashboardViewType>(dashboardViewTypeId);
  useEffect(() =>
  {
    dashboardViewTypeIdRef.current = dashboardViewTypeId;
  }, [dashboardViewTypeId]);
  const columnWidthRef = useRef<number>(columnWidth);
  useEffect(() =>
  {
    columnWidthRef.current = columnWidth;
  }, [columnWidth]);
  const pinnedDashboardsRef = useRef<Dashboard[]>([]);
  useEffect(() =>
  {
    pinnedDashboardsRef.current = pinnedDashboards;
  }, [pinnedDashboards]);

  // Helper to save dashboard selection state to C2_currentDashboard
  const saveDashboardState = useCallback(
    (dashboard: Dashboard, viewId: DashboardViewType, gridSize: number) =>
    {
      const pinned = pinnedDashboardsRef.current;
      const index = pinned.findIndex(d => d.DashboardId === dashboard.DashboardId);
      dashboardStorage.updateDashboard({
        projectId: 0,
        configValueId: configValueId,
        index: index >= 0 ? index : 0,
        dashboardId: dashboard.DashboardId,
        DashboardViewId: viewId,
        GridSize: gridSize,
        lastModified: Date.now(),
      });
    },
    [configValueId],
  );

  // Update browser tab title when dashboard changes
  useEffect(() =>
  {
    document.title = currentDashboard?.Name || 'Dashboards';
  }, [currentDashboard]);

  // Save dashboard visit to search history
  useEffect(() =>
  {
    if (currentDashboard?.DashboardId && currentDashboard?.Name)
    {
      searchHistory.addDashboardVisit(
        currentDashboard.DashboardId,
        currentDashboard.Name,
        null,
        sectionName,
      );
    }
  }, [currentDashboard, sectionName]);

  // TabControl handlers
  const handleSetDashboards = useCallback(
    (
      _allDashboards: Dashboard[],
      pinned: Dashboard[],
      _notPinned: Dashboard[],
      current: Dashboard | undefined,
      _isInitial?: boolean,
    ) =>
    {
      setPinnedDashboards(pinned);
      if (current)
      {
        setCurrentDashboard(current);
      }
    },
    [],
  );

  const handleSetDashboard = useCallback((dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
    setSearchText('');
  }, []);

  const handleAddDashboard = useCallback((_event: React.MouseEvent) => {}, []);

  const handleUpdateDashboard = useCallback(
    async (_event: React.MouseEvent, dashboard: Dashboard) =>
    {
      try
      {
        const updated = await dashboardApi.updateDashboard(dashboard);
        setCurrentDashboard(prev =>
          prev?.DashboardId === updated.DashboardId ? { ...prev, ...updated } : prev,
        );
        setPinnedDashboards(prev => prev.map(d =>
          d.DashboardId === updated.DashboardId ? { ...d, ...updated } : d,
        ));
      }
      catch (error)
      {
        console.error('Error updating dashboard:', error);
      }
    },
    [],
  );

  const handlePinDashboard = useCallback(
    async (_event: React.MouseEvent, dashboard: Dashboard) =>
    {
      // Act on any dashboard with a pin-actionable flag, not just the shared one
      if (!dashboard.IsPineableDashboard && !dashboard.IsPinedDashboard) return;
      try
      {
        if (dashboard.IsPineableDashboard)
        {
          const order = (pinnedDashboards?.length || 0) + 1;
          await dashboardService.AddPinedDashboard(dashboard.DashboardId, order);
          await dashboardService.AddPinedTabDashboard(
            dashboard.DashboardId,
            order,
            configValueId,
            0,
            true,
            socketConnectionId,
          );
        }
        else if (dashboard.IsPinedDashboard)
        {
          await dashboardService.DeletePinedDashboard(dashboard.DashboardId);
          await dashboardService.DeletePinedTabDashboard(
            configValueId,
            dashboard.DashboardId,
            0,
            socketConnectionId,
          );

          // Forget the shared-dashboard tracking so it doesn't reappear after refresh
          if (sharedDashboardId === dashboard.DashboardId)
          {
            navigate(location.pathname, { replace: true, state: {} });
          }
        }
      }
      catch (err)
      {
        console.error('Error updating pin state:', err);
      }
    },
    [pinnedDashboards, configValueId, socketConnectionId, sharedDashboardId, navigate, location.pathname],
  );

  const handleDeleteDashboard = useCallback(
    (_event: React.MouseEvent, _dashboard: Dashboard) => {},
    [],
  );

  const handleOpenTabDashboard = useCallback((_event: React.MouseEvent, dashboard: Dashboard) =>
  {
    setCurrentDashboard(dashboard);
  }, []);

  const attachReactFunctions = useCallback((functions: ReactFunctions) =>
  {
    reactFunctionsRef.current = functions;
  }, []);

  const onDuplicated = useCallback((newDashboardId?: number) =>
  {
    if (newDashboardId !== undefined)
    {
      reactFunctionsRef.current?.reloadDashboard(false, newDashboardId);
    }
    else
    {
      reactFunctionsRef.current?.reloadDashboard(true);
    }
  }, []);

  // TabControl configuration
  const tabControlConfig: TabControlConfig = {
    api: getApiConfig(),
    configValueId: configValueId,
    projectId: undefined,
    DashboardId: currentDashboard?.DashboardId,
    isOrganize: false,
    isBookmarks: configValueId === getBookmarksConfigValueId(),
    isProjectDetail: false,
    isDashboardShared: !!sharedDashboardId,
    dashboardSharedId: sharedDashboardId ?? undefined,
    recycleBinDashboard: false,
    canEdit: true,
    isAdmin: IsAdmin(),
    webUrl: import.meta.env.VITE_WEB_URL || 'http://localhost/Corgee.WebClient/',
    functions: {
      setDashboards: handleSetDashboards,
      setDashboard: handleSetDashboard,
      addDashboard: handleAddDashboard,
      updateDashboard: handleUpdateDashboard,
      pinDashboard: handlePinDashboard,
      deleteDashboard: handleDeleteDashboard,
      OpenTabDashboard: handleOpenTabDashboard,
      attachReactFunctions: attachReactFunctions,
      onDuplicated,
      getSocketConnectionId: () => socketConnectionId,
    },
  };

  // True when the active tab is the shared dashboard and it has no public access
  const isCurrentSharedNoPermission =
    !!sharedDashboardId &&
    currentDashboard?.DashboardId === sharedDashboardId &&
    isSharedPublic === false;

  // DashboardGroups callbacks
  const handleDataLoaded = useCallback((_groups: DashboardGroupData[]) => {}, []);

  const handleGroupsError = useCallback((error: Error) =>
  {
    console.error('Dashboard groups error:', error);
  }, []);

  const handleCardMoved = useCallback(
    (_conversationId: number, _fromGroupId: number, _toGroupId: number) => {},
    [],
  );

  const handleOpenInfoWindow = useCallback(
    (conversation: ConversationDetail) =>
    {
      clearConversationHighlight(conversation.ConversationId);
      // Track person visit when in Users section (configValueId === 15)
      if (configValueId === 15 && conversation.ConversationId)
      {
        searchHistory.addPersonVisit(
          conversation.ConversationId,
          conversation.Title,
          conversation.ImageName,
        );
      }
      openInfoWindow(conversation.ConversationId, undefined, conversation);
    },
    [configValueId, openInfoWindow, clearConversationHighlight],
  );

  // Apply persisted view and grid size to local state
  const applyDashboardView = useCallback(
    (dashboardId: number, viewType: DashboardViewType, apiGridSize: number) =>
    {
      setCurrentDashboard(prev => prev && prev.DashboardId === dashboardId ? {
        ...prev,
        DashboardViewTypeId: viewType,
        GridSize: apiGridSize,
      } : prev);

      setPinnedDashboards(prev => prev.map(d =>
        d.DashboardId === dashboardId
          ? { ...d, DashboardViewTypeId: viewType, GridSize: apiGridSize }
          : d,
      ));

      reactFunctionsRef.current?.updateDashboardView(dashboardId, viewType, apiGridSize);
    },
    [],
  );

  // Handle view change: update state, save to C2_currentDashboard, call API
  const handleViewChange = useCallback(
    (viewType: DashboardViewType) =>
    {
      setDashboardViewTypeId(viewType);
      if (!currentDashboard) return;
      const gridSize = columnWidthRef.current;
      saveDashboardState(currentDashboard, viewType, gridSize);

      persistDashboardView(currentDashboard.DashboardId, viewType, gridSize, apiGridSize =>
      {
        applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
      });
    },
    [currentDashboard, saveDashboardState, persistDashboardView, applyDashboardView],
  );

  // Persist column resize (debounced)
  const handleColumnWidthChange = useCallback(
    (newWidth: number) =>
    {
      setColumnWidth(newWidth);
      if (!currentDashboard) return;

      const viewType = dashboardViewTypeIdRef.current;
      saveDashboardState(currentDashboard, viewType, newWidth);

      persistDashboardViewDebounced(
        currentDashboard.DashboardId,
        viewType,
        newWidth,
        apiGridSize =>
        {
          applyDashboardView(currentDashboard.DashboardId, viewType, apiGridSize);
        },
      );
    },
    [currentDashboard, saveDashboardState, persistDashboardViewDebounced, applyDashboardView],
  );

  // Build search text from filters
  const buildSearchText = useCallback(() =>
  {
    return searchText.trim();
  }, [searchText]);

  return (
    <div className="dashboards-config-page h-full flex flex-col">
      {/* TabControl Section */}
      <div className="border border-gray-200 rounded-lg bg-gray-50">
        <TabControl key={configValueId} config={tabControlConfig} />
      </div>

      {/* Filters Section */}
      <div
        className="flex items-center justify-between gap-2"
        style={{ paddingLeft: '15px', paddingRight: '15px' }}
      >
        <div className="flex items-center gap-2">
          <FilterBar
            key={currentDashboard?.DashboardId ?? 0}
            filter={filter}
            onChangeFilter={setFilter}
            loading={filterLoading}
            filterOptions={filterOptions}
            error={filterError}
            filtersSelected={filtersSelected}
            onFilterOpened={handleFilterOpened}
            onFilterSelect={handleFilterSelect}
            onFilterUnselect={handleFilterUnselect}
            isApplyForAll={isApplyForAll}
            onApplyFilters={handleApplyFiltersWrapper}
          />
          <SearchBar filterSearchCard={searchText} onChangeSearchFilterCards={setSearchText} />
        </div>
        <div className="flex items-center gap-1">
          {dashboardViewTypeId !== 3 &&
            dashboardViewTypeId !== 4 &&
            dashboardViewTypeId !== 5 &&
            dashboardViewTypeId !== 6 &&
            dashboardViewTypeId !== 7 &&
            dashboardViewTypeId !== 9 && (
            <input
              type="range"
              min="250"
              max="800"
              value={columnWidth}
              onChange={e => handleColumnWidthChange(Number(e.target.value))}
              className="w-24 h-1 custom-slider"
              style={{ accentColor: '#62aacb' }}
            />
          )}
          {dashboardViewTypeId === 7 && (
            <>
              <label
                className="flex items-center gap-1 text-xs cursor-pointer"
                style={{
                  color: isDark ? '#6e6860' : 'rgba(0,0,0,0.6)',
                  fontSize: '10px',
                  paddingRight: '15px',
                  paddingTop: '7px',
                }}
              >
                <input
                  type="checkbox"
                  checked={enableWeekend}
                  onChange={e => setEnableWeekend(e.target.checked)}
                  className="w-3 h-3"
                />
                Enable Weekend
              </label>
              <select
                className="text-xs border rounded px-1 py-0.5"
                style={{
                  color: isDark ? '#a9a093' : 'rgba(0,0,0,0.7)',
                  backgroundColor: isDark ? '#3a3c3c' : undefined,
                  borderColor: isDark ? '#4a4c4c' : undefined,
                }}
                value={selectedLayerId}
                onChange={e => setSelectedLayerId(Number(e.target.value))}
              >
                {ganttLayers.map(layer => (
                  <option key={layer.LayerId} value={layer.LayerId}>
                    {layer.Name}
                  </option>
                ))}
              </select>
            </>
          )}
          {(dashboardViewTypeId === 4 || dashboardViewTypeId === 11) && (
            <button
              className="view-bar-btn"
              title="Export to Excel"
              onClick={() => gridExportRef.current?.exportToExcel()}
            >
              <i className="material-icons">cloud_download</i>
              <span>Export</span>
            </button>
          )}
          {dashboardViewTypeId === 9 && (
            <>
              <div
                style={{ lineHeight: '24px', color: 'rgba(0, 0, 0, 0.45)' }}
                className="view-bar-button enable-help-tooltip"
                ht-name="Hierarchical cards are not shown in this view"
              >
                <i className="fa fa-exclamation-circle fa-2"></i>
              </div>
              <button
                className="view-bar-btn"
                title="Export to Excel"
                onClick={() => phaseDetailsExportRef.current?.exportToExcel()}
              >
                <i className="material-icons">cloud_download</i>
                <span>Export</span>
              </button>
            </>
          )}
          {dashboardViewTypeId === 8 && (
            <button
              className="view-bar-btn"
              title="Export to Excel"
              onClick={() => phaseDetailsV2ExportRef.current?.exportToExcel()}
            >
              <i className="material-icons">cloud_download</i>
              <span>Export</span>
            </button>
          )}
          {dashboardViewTypeId === 4 && (
            <button className="view-bar-btn" title="Import" onClick={() => {}}>
              <i className="material-icons">cloud_upload</i>
              <span>Import</span>
            </button>
          )}
          {dashboardViewTypeId === 6 && (
            <div className="flex items-center gap-1.5">
              <input
                id="chkShowAllGallery"
                type="checkbox"
                checked={showAllGalleryImages}
                onChange={e => setShowAllGalleryImages(e.target.checked)}
                className="cursor-pointer"
              />
              <label htmlFor="chkShowAllGallery" className="cursor-pointer select-none text-xs font-normal text-gray-600 dark:text-gray-300 whitespace-nowrap">
                Show all attached images
              </label>
            </div>
          )}
          <DashboardViewSelector
            currentViewTypeId={dashboardViewTypeId}
            onViewChange={handleViewChange}
          />
          <RefreshNotificationButton
            hasPendingUpdates={hasPendingUpdates}
            pendingCount={pendingCount}
            onClick={triggerRefresh}
            message={
              pendingDashboardList
                ? 'Your dashboards have changed, click here to refresh'
                : pendingOrderDashboard
                  ? 'Your dashboards have been reordered, click here to refresh'
                  : undefined
            }
          />
        </div>
      </div>

      {/* No-permission message for shared dashboard */}
      {isCurrentSharedNoPermission && (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-400 mt-8">
          <i className="fa fa-lock" style={{ fontSize: '48px' }}></i>
          <p className="text-base">You don't have permission to view this dashboard.</p>
        </div>
      )}

      {/* CalendarView Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 3 && (
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          <CalendarView
            key={`cal-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            configValueId={configValueId}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
            onRegisterRefresh={fn =>
            {
              dashboardGroupsRefreshRef.current = fn;
            }}
          />
        </div>
      )}

      {/* GalleryView Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 6 && (
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          <GalleryView
            key={`gallery-config-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
            showAllImages={showAllGalleryImages}
          />
        </div>
      )}

      {/* FilesView Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 5 && (
        <div className="flex-1 min-h-0 overflow-auto mt-2">
          <FilesView
            key={`files-config-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            onOpenInfoWindow={id => openInfoWindow(id)}
          />
        </div>
      )}

      {/* GridView Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 4 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <GridView
            ref={gridExportRef}
            key={`grid-config-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            configValueId={configValueId}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={handleOpenInfoWindow}
          />
        </div>
      )}

      {/* GanttView Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 7 && (
        <div className="gantt-wrapper gantt-container">
          <Gantt
            key={`gantt-config-${configValueId}-${currentDashboard.DashboardId}-layer${selectedLayerId}-weekend${enableWeekend}-${filtersJson}-${searchText}`}
            dashboard={{
              DashboardId: currentDashboard.DashboardId,
              DashboardViewTypeId: 7,
              LayersToHide: (currentDashboard as any).LayersToHide || '',
              PageSize: 40,
              PageNumber: 1,
              Filters: filtersJson,
              SearchText: buildSearchText(),
              ...extraFilterParams,
            }}
            config={
              {
                apiUrl: getApiConfig().url,
                authToken: getApiConfig().token,
                selectedLayerId: selectedLayerId,
                WorkWeekend: enableWeekend,
                openInfoWindow: (conversationId: number) => openInfoWindow(conversationId),
                loadGanttLayers: (layers: any[]) => setGanttLayers(layers),
              } as GanttConfig
            }
          />
        </div>
      )}

      {/* PhaseDetails Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 9 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <PhaseDetails
            ref={phaseDetailsExportRef}
            key={`phase-config-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            configValueId={configValueId}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={handleOpenInfoWindow}
          />
        </div>
      )}

      {/* PhaseDetailsV2 Section */}
      {!isCurrentSharedNoPermission && currentDashboard && dashboardViewTypeId === 8 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <PhaseDetailsV2
            ref={phaseDetailsV2ExportRef}
            key={`phase-v2-config-${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            dashboardName={currentDashboard.Name}
            configValueId={configValueId}
            filters={filtersJson}
            searchText={buildSearchText()}
            extraFilterParams={extraFilterParams}
            isActive={true}
            onOpenInfoWindow={handleOpenInfoWindow}
          />
        </div>
      )}

      {/* DashboardGroups Section */}
      {!isCurrentSharedNoPermission &&
        currentDashboard &&
        dashboardViewTypeId !== 3 &&
        dashboardViewTypeId !== 4 &&
        dashboardViewTypeId !== 5 &&
        dashboardViewTypeId !== 6 &&
        dashboardViewTypeId !== 7 &&
        dashboardViewTypeId !== 9 &&
        dashboardViewTypeId !== 8 && (
        <div className="flex-1 min-h-0 overflow-hidden mt-2">
          <DashboardGroups
            key={`${configValueId}-${currentDashboard.DashboardId}`}
            dashboardId={currentDashboard.DashboardId}
            projectId=""
            filters={filtersJson}
            extraFilterParams={extraFilterParams}
            layersToHide={isApplyForAll ? '' : undefined}
            searchText={buildSearchText()}
            pageSize={pageSize}
            cardViewMode={cardViewMode}
            columnWidth={columnWidth}
            dashboardViewTypeId={dashboardViewTypeId}
            configValueId={configValueId}
            onDataLoaded={handleDataLoaded}
            onError={handleGroupsError}
            onCardMoved={handleCardMoved}
            onOpenInfoWindow={handleOpenInfoWindow}
            onRegisterRefresh={fn =>
            {
              dashboardGroupsRefreshRef.current = fn;
            }}
            pendingGroupIds={pendingGroupIds}
            pendingConversationIds={pendingConversationIds}
          />
        </div>
      )}
    </div>
  );
};

export default DashboardsByConfigPage;
